import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { EmailService } from '../../../../../service/emailService';
import { EmailTemplateService } from '../../../../../service/emailTemplateService';

export default function SendInvoiceEmailDialog({
  open,
  recipient,
  entityId,
  invoiceNumber,
  onClose,
  onSuccess
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [error, setError] = useState('');

  const invoiceTemplates = useMemo(
    () => templates.filter((t) => t.communicationType === 'invoice'),
    [templates]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const load = async () => {
      setIsBootstrapping(true);
      setError('');
      setTemplates([]);
      setSelectedTemplate('');
      setSubject('');
      setMessage('');

      try {
        const response = await EmailTemplateService.getTemplates();
        setTemplates(response?.data || []);
      } catch (loadError) {
        setError(loadError.message || 'Failed to load templates');
      } finally {
        setIsBootstrapping(false);
      }
    };

    load();
  }, [open]);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);

    if (!templateId) {
      setSubject('');
      setMessage('');
      return;
    }

    const template = invoiceTemplates.find((item) => String(item.id) === String(templateId));
    if (!template) {
      return;
    }

    setSubject(template.subject || '');

    let body = template.body || '';
    if (template.footer) {
      body += `\n\n${template.footer}`;
    }
    setMessage(body);
  };

  const handleSend = async () => {
    if (!recipient?.recipientId) {
      setError('Customer is missing');
      return;
    }

    if (!recipient?.email) {
      setError('Customer email is missing');
      return;
    }

    if (!entityId) {
      setError('Invoice must be saved before sending email');
      return;
    }

    if (!subject.trim()) {
      setError('Email subject is required');
      return;
    }

    if (!message.trim()) {
      setError('Email body is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        customerId: recipient.recipientId,
        toEmail: recipient.email,
        invoiceId: entityId,
        communicationType: 'invoice',
        templateId: selectedTemplate || undefined,
        subject: subject.trim(),
        message: message.trim()
      };

      const response = await EmailService.sendEmail(payload);
      if (response?.code === 0) {
        onSuccess?.('Invoice email sent successfully');
        onClose();
        return;
      }

      setError(response?.message || 'Failed to send email');
    } catch (sendError) {
      setError(sendError.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Invoice Email</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              To: {recipient?.name || 'Customer'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Email: {recipient?.email || '-'}
            </Typography>
            {invoiceNumber && (
              <Typography variant="caption" color="text.secondary" display="block">
                Invoice #: {invoiceNumber}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              Invoice PDF will be attached automatically.
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth size="small" disabled={isBootstrapping || loading}>
            <InputLabel>Template (Optional)</InputLabel>
            <Select
              value={selectedTemplate}
              onChange={(event) => handleTemplateChange(event.target.value)}
              label="Template (Optional)"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {invoiceTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.templateName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            fullWidth
            size="small"
            disabled={isBootstrapping || loading}
          />

          <TextField
            label="Message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            multiline
            rows={6}
            fullWidth
            disabled={isBootstrapping || loading}
          />

          {isBootstrapping && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSend} variant="contained" disabled={loading || isBootstrapping}>
          {loading ? <CircularProgress size={22} /> : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
